import { cn } from '@/lib/utils';

interface SectionSpinnerProps {
  /**
   * `block` — centered spinner inside a min-height block (for initial loads).
   * `inline` — small spinner sized for placement next to text (for background fetches).
   * `overlay` — absolute-positioned tinted overlay covering its relative parent
   *   (for background fetches while stale content remains visible underneath).
   */
  variant: 'block' | 'inline' | 'overlay';
  /** Optional label shown next to the spinner. */
  label?: string;
  className?: string;
}

/**
 * Consistent spinner UX for the replenishment page. Uses the existing spinner
 * style (border + animate-spin) so visuals stay aligned with the rest of the
 * page.
 */
export function SectionSpinner({
  variant,
  label,
  className,
}: SectionSpinnerProps) {
  if (variant === 'inline') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 text-xs text-gray-500',
          className,
        )}
      >
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        {label && <span>{label}</span>}
      </span>
    );
  }

  if (variant === 'overlay') {
    return (
      <div
        className={cn(
          'pointer-events-none absolute inset-0 z-10 flex items-start justify-center pt-4',
          className,
        )}
      >
        <span className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs text-gray-600 shadow-sm ring-1 ring-gray-200 backdrop-blur-sm">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          {label && <span>{label}</span>}
        </span>
      </div>
    );
  }

  // block
  return (
    <div
      className={cn(
        'flex items-center justify-center gap-3 py-12 text-sm text-gray-500',
        className,
      )}
    >
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      {label && <span>{label}</span>}
    </div>
  );
}
